<template>
  <div class="content-panel media" v-if="content">
    <figure class="media-left">
      <BaseImage :w="64" :h="64" :src="imgSrc" />
    </figure>
    <div class="media-content">
      <p>
        <strong>{{ ls(content.title) }}</strong>
        <UiButtonIcon
          v-if="editable"
          tag="button"
          class="jps-icon-button jps-sub-orange"
          icon="mdi/pencil"
          icon-size="size-6"
          @click="editMeta()"
        />
        <br />
        {{ lc(codeRightsType, content.contentsRightsType) }}
        {{ ls(content.description) }}
        <br />
        <span v-if="editable || content.images.length > 1">
          全{{ content.images.length }}画像
          <button class="jps-tag" @click="showImage = !showImage">
            全て表示
            <BaseIcon
              class="jps-icon-button"
              :name="showImage ? 'mdi/menu-up' : 'mdi/menu-down'"
            />
          </button>
        </span>
      </p>
      <template v-if="showImage">
        <div class="media" v-for="image in content.images" :key="image.id">
          <figure class="media-left">
            <BaseImage :w="64" :h="64" :src="thumbnail(image)" />
          </figure>
          <div class="media-content">
            <p>
              {{ image.fileName }}
              <br />
              サイズ：{{ image.width }}x{{ image.height }}px
              <br />
              <small v-if="editable">
                <a :href="full(image)" target="_blank"
                  >オリジナル画像のダウンロード</a
                >
              </small>
            </p>
          </div>
          <div class="media-right">
            <button
              v-if="selectable"
              class="jps-button"
              @click="selectImage(image)"
            >
              この画像を選ぶ
            </button>
            <button
              v-if="editable"
              class="jps-tag jps-sub-orange"
              @click="updateImageLocal(image)"
            >
              画像の差し替え
            </button>
            <button
              v-if="editable"
              class="jps-tag jps-sub-orange"
              @click="deleteImageLocal(image)"
            >
              画像の削除
            </button>
          </div>
        </div>
        <div class="media" v-if="editable">
          <div class="media-content">
            <button class="jps-button" @click="addImageLocal()">
              画像を追加する
            </button>
          </div>
        </div>
      </template>
    </div>
    <div class="media-right">
      <button
        v-if="selectable && content.images.length"
        class="jps-button"
        @click="selectImage(content.images[0])"
      >
        この画像を選ぶ
      </button>
      <UiButtonIcon
        v-if="deletable"
        tag="button"
        class="jps-icon-button"
        icon="mdi/delete"
        @click="deleteContentLocal()"
      />
      <br />
      <small>最終更新{{ ldate(content.edit.date) }}</small>
    </div>
    <UiConfirmModal ref="confirmModal" />
    <ContentEditModal ref="contentEditModal" />
  </div>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { UiConfirmModal, ContentEditModal } from '#components'

const { ls, lst, lc, ldate } = useI18n()
const { $getCodeRightsType } = useNuxtApp()
const { setLoading, loadingExecuteAndNotify } = useLoader()
const { contentThumbnailUrl, contentImageUrl, addImage, updateImage, deleteImage, deleteContent, updateContentMeta } = useContent()
const dataStore = useDataStore()

interface Props {
  editable?: boolean,
  deletable?: boolean,
  selectable?: boolean,
  content: Content
}
const props = withDefaults(defineProps<Props>(),{
  editable: false,
  deletable: false,
  selectable: false,
})

const emits = defineEmits(['select', 'update', 'delete'])

const codeRightsType = $getCodeRightsType()
const showImage:Ref<boolean> = ref(false)
const imageForceUpdateFlag = ref('')

const imgSrc = computed(() => {
  if (props.content && props.content.images.length > 0) {
    return (
      contentThumbnailUrl(props.content.id, props.content.images[0].id) +
      (imageForceUpdateFlag.value ? '?' + imageForceUpdateFlag.value : '')
    )
  }
  return null
})

const selectImage = (image) => {
  emits('select', { content: props.content, image })
}

const thumbnail = (image) => {
  return (
    contentThumbnailUrl(props.content.id, image.id) +
    (imageForceUpdateFlag.value ? '?' + imageForceUpdateFlag.value : '')
  )
}

const full = (image) => {
  return contentImageUrl(props.content.id, image.id)
}

// 編集系
const confirmModal = ref<InstanceType<typeof UiConfirmModal>>()
const deleteImageLocal = async (image: Image) => {
  try {
    const confirm = await confirmModal.value.openAwait({
      message: dapLangGlobal.deleteConfirm,
      messageVars: [image.fileName]
    })
    if (!confirm) return
    let content = await loadingExecuteAndNotify(
      lst(dapLangGlobal.deleteSuccess, image.fileName),
      deleteImage(props.content.id, image.id)
    )
    dataStore.put('content', content.id, content)
    emits('update', content)
  } catch (e) {}
}

const deleteContentLocal = async () => {
  try {
    const confirm = await confirmModal.value.openAwait({
      message: dapLangGlobal.deleteConfirm,
      messageVars: [ls(props.content.title)]
    })
    if (!confirm) return
    await loadingExecuteAndNotify(
      lst(dapLangGlobal.deleteSuccess, ls(props.content.title)),
      deleteContent(props.content)
    )
    emits('delete', props.content)
  } catch (e) {}
}

const contentEditModal = ref<InstanceType<typeof ContentEditModal>>()
const editMeta = async () => {
  try {
    const content: Content = await contentEditModal.value.openAwait({
      mode: 'meta',
      content: props.content
    }) as Content
    if (!content) return
    setLoading(true)
    await updateContentMeta(content)
    dataStore.put('content', content.id, content)
    emits('update', content)
  } catch (e) {}
  setLoading(false)
}

const updateImageLocal = async (image: Image) => {
  try {
    const files: File[] = await contentEditModal.value.openAwait({
      mode: 'image',
      single: true
    }) as File[]
    if (!files) return
    setLoading(true)
    const content = await updateImage(
      props.content.id,
      image.id,
      files[0]
    )
    dataStore.put('content', content.id, content)
    emits('update', content)
    imageForceUpdateFlag.value = luId()
  } catch (e) {}
  setLoading(false)
}

const addImageLocal = async () => {
  try {
    const files: File[] = await contentEditModal.value.openAwait({
      mode: 'image'
    }) as File[]
    if (!files) return
    setLoading(true)
    let content = null
    for (let i = 0; i < files.length; i++) {
      content = await addImage(props.content.id, files[i])
    }
    dataStore.put('content', content.id, content)
    emits('update', content)
  } catch (e) {}
  setLoading(false)
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.content-panel {
  /* border: 1px solid $jps-gray-300; */
}
</style>
